import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "shared/form_helpers"
import { sendSuccessNotification, sendFailureNotification } from "shared/toast_notifications"

// Connects to data-controller="facility-import"
export default class extends Controller {
    static targets = [
        "dropzone",
        "fileInput",
        "fileInfo",
        "fileName",
        "fileSize",
        "uploadButton",
        "progressCard",
        "progressBar",
        "progressText",
        "progressPercentage",
        "processedCount",
        "totalCount",
        "createdCount",
        "updatedCount",
        "errorCount",
        "errorCard",
        "errorList",
        "successCard",
        "successMessage"
    ]

    connect() {
        this.selectedFile = null
        this.pollingInterval = null
        this.currentTaskId = null
    }

    disconnect() {
        this.stopPolling()
    }

    // Template download
    downloadTemplate(event) {
        event.preventDefault()
        window.location.href = '/settings/facilities/download_template'
    }

    // Export data
    exportData(event) {
        event.preventDefault()
        window.location.href = '/settings/facilities/export_facilities'
        sendSuccessNotification('Export started. Your download will begin shortly.')
    }

    // Drag and drop handlers
    handleDragOver(event) {
        event.preventDefault()
        event.stopPropagation()
        this.dropzoneTarget.style.borderColor = '#0d6efd'
        this.dropzoneTarget.style.backgroundColor = '#e7f1ff'
    }

    handleDragLeave(event) {
        event.preventDefault()
        event.stopPropagation()
        this.dropzoneTarget.style.borderColor = '#dee2e6'
        this.dropzoneTarget.style.backgroundColor = '#f8f9fa'
    }

    handleDrop(event) {
        event.preventDefault()
        event.stopPropagation()
        
        this.dropzoneTarget.style.borderColor = '#dee2e6'
        this.dropzoneTarget.style.backgroundColor = '#f8f9fa'
        
        const files = event.dataTransfer.files
        if (files.length > 0) {
            this.handleFile(files[0])
        }
    }

    triggerFileInput(event) {
        event.preventDefault()
        if (this.hasFileInputTarget) {
            this.fileInputTarget.click()
        } else {
            console.error('fileInputTarget not found!')
        }
    }

    handleFileSelect(event) {
        const files = event.target.files
        if (files.length > 0) {
            this.handleFile(files[0])
        }
    }

    handleFile(file) {
        // Validate file type
        if (!file.name.endsWith('.csv')) {
            sendFailureNotification('Please select a CSV file')
            return
        }

        // Validate file size (10MB limit)
        const maxSize = 10 * 1024 * 1024 // 10MB in bytes
        if (file.size > maxSize) {
            sendFailureNotification('File size exceeds 10MB limit')
            return
        }

        this.selectedFile = file
        
        // Display file info
        this.fileNameTarget.textContent = file.name
        this.fileSizeTarget.textContent = this.formatFileSize(file.size)
        this.fileInfoTarget.classList.remove('d-none')
        this.uploadButtonTarget.classList.remove('d-none')
        
        // Hide any previous results
        this.hideResults()
    }

    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    }

    async uploadFile(event) {
        event.preventDefault()
        
        if (!this.selectedFile) {
            sendFailureNotification('Please select a file first')
            return
        }

        // Hide previous results
        this.hideResults()
        
        // Disable upload button
        this.uploadButtonTarget.disabled = true
        this.uploadButtonTarget.innerHTML = '<i class="bi bi-hourglass-split me-2"></i>Uploading...'
        
        try {
            const formData = new FormData()
            formData.append('file', this.selectedFile)
            
            const response = await fetch('/settings/facilities/upload_import', {
                method: 'POST',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })
            
            const data = await response.json()
            
            if (response.ok) {
                // Import started successfully
                this.currentTaskId = data.task_id
                sendSuccessNotification(data.message)
                
                // Show progress card and start polling
                this.showProgressCard()
                this.startPolling()
            } else {
                // Validation or processing error
                if (data.errors && Array.isArray(data.errors)) {
                    this.displayValidationErrors(data.errors)
                } else {
                    sendFailureNotification(data.error || 'Import failed')
                }
                this.resetUploadButton()
            }
        } catch (error) {
            console.error('Upload error:', error)
            sendFailureNotification('Failed to upload file. Please try again.')
            this.resetUploadButton()
        }
    }

    resetUploadButton() {
        this.uploadButtonTarget.disabled = false
        this.uploadButtonTarget.innerHTML = '<i class="bi bi-upload me-2"></i>Upload File'
    }

    showProgressCard() {
        this.progressCardTarget.classList.remove('d-none')
        this.progressBarTarget.style.width = '0%'
        this.progressPercentageTarget.textContent = '0%'
        this.progressTextTarget.textContent = 'Starting import...'
    }

    startPolling() {
        // Poll every 2 seconds
        this.pollingInterval = setInterval(() => {
            this.checkImportStatus()
        }, 2000)
    }

    stopPolling() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval)
            this.pollingInterval = null
        }
    }

    async checkImportStatus() {
        if (!this.currentTaskId) {
            this.stopPolling()
            return
        }

        try {
            const response = await fetch(`/settings/facilities/import_status/${this.currentTaskId}`, {
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                }
            })
            
            if (!response.ok) {
                throw new Error('Failed to fetch status')
            }
            
            const data = await response.json()
            
            // Update progress display
            this.updateProgress(data)
            
            // Check if import is complete
            if (data.status === 'completed' || data.status === 'failed') {
                this.stopPolling()
                this.handleImportComplete(data)
            }
        } catch (error) {
            console.error('Status check error:', error)
            this.stopPolling()
            sendFailureNotification('Failed to check import status')
        }
    }

    updateProgress(data) {
        const percentage = data.percentage || 0
        
        // Update progress bar
        this.progressBarTarget.style.width = `${percentage}%`
        this.progressBarTarget.setAttribute('aria-valuenow', percentage)
        this.progressPercentageTarget.textContent = `${percentage}%`
        
        // Update progress text
        this.progressTextTarget.textContent = data.status || 'Processing...'
        
        // Update counts
        this.processedCountTarget.textContent = data.processed_rows || 0
        this.totalCountTarget.textContent = data.total_rows || 0
        this.createdCountTarget.textContent = data.created_count || 0
        this.updatedCountTarget.textContent = data.updated_count || 0
        this.errorCountTarget.textContent = data.error_count || 0
    }

    handleImportComplete(data) {
        const errorCount = data.error_count || 0
        const createdCount = data.created_count || 0
        const updatedCount = data.updated_count || 0
        
        if (data.status === 'completed') {
            if (errorCount > 0) {
                // Partial success
                this.displayPartialSuccess(createdCount, updatedCount, errorCount, data.errors)
            } else {
                // Full success
                this.displaySuccess(createdCount, updatedCount)
            }
        } else if (data.status === 'failed') {
            // Complete failure
            this.displayFailure(data.error_message, data.errors)
        }
        
        // Reset for next import
        this.resetUploadButton()
        this.selectedFile = null
        this.fileInfoTarget.classList.add('d-none')
        this.uploadButtonTarget.classList.add('d-none')
        this.fileInputTarget.value = ''
    }

    displaySuccess(createdCount, updatedCount) {
        this.successMessageTarget.textContent = 
            `Import complete! Created ${createdCount} locations, Updated ${updatedCount} locations.`
        this.successCardTarget.classList.remove('d-none')
        sendSuccessNotification('Import completed successfully!')
    }

    displayPartialSuccess(createdCount, updatedCount, errorCount, errors) {
        this.successMessageTarget.textContent = 
            `Import complete with ${errorCount} errors. Created ${createdCount} locations, Updated ${updatedCount} locations.`
        this.successCardTarget.classList.remove('d-none')
        
        if (errors && errors.length > 0) {
            this.displayValidationErrors(errors)
        }
        
        sendSuccessNotification(`Import completed with ${errorCount} errors. Check details below.`)
    }

    displayFailure(errorMessage, errors) {
        sendFailureNotification(errorMessage || 'Import failed. Please review errors and try again.')
        
        if (errors && errors.length > 0) {
            this.displayValidationErrors(errors)
        }
    }

    displayValidationErrors(errors) {
        this.errorListTarget.innerHTML = ''
        
        errors.forEach((error, index) => {
            const errorItem = document.createElement('div')
            errorItem.className = 'accordion-item'
            
            const errorHeader = document.createElement('h2')
            errorHeader.className = 'accordion-header'
            errorHeader.id = `errorHeading${index}`
            
            const errorButton = document.createElement('button')
            errorButton.className = 'accordion-button collapsed'
            errorButton.type = 'button'
            errorButton.setAttribute('data-bs-toggle', 'collapse')
            errorButton.setAttribute('data-bs-target', `#errorCollapse${index}`)
            errorButton.setAttribute('aria-expanded', 'false')
            errorButton.setAttribute('aria-controls', `errorCollapse${index}`)
            
            // Format error header
            if (error.row) {
                errorButton.textContent = `Row ${error.row}: ${error.message || 'Validation error'}`
            } else {
                errorButton.textContent = error.message || 'Validation error'
            }
            
            errorHeader.appendChild(errorButton)
            
            const errorCollapse = document.createElement('div')
            errorCollapse.id = `errorCollapse${index}`
            errorCollapse.className = 'accordion-collapse collapse'
            errorCollapse.setAttribute('aria-labelledby', `errorHeading${index}`)
            errorCollapse.setAttribute('data-bs-parent', '#errorAccordion')
            
            const errorBody = document.createElement('div')
            errorBody.className = 'accordion-body'
            
            // Format error details
            let errorDetails = '<ul class="mb-0">'
            if (error.field) {
                errorDetails += `<li><strong>Field:</strong> ${error.field}</li>`
            }
            if (error.value) {
                errorDetails += `<li><strong>Value:</strong> ${error.value}</li>`
            }
            if (error.message) {
                errorDetails += `<li><strong>Error:</strong> ${error.message}</li>`
            }
            errorDetails += '</ul>'
            
            errorBody.innerHTML = errorDetails
            errorCollapse.appendChild(errorBody)
            
            errorItem.appendChild(errorHeader)
            errorItem.appendChild(errorCollapse)
            
            this.errorListTarget.appendChild(errorItem)
        })
        
        this.errorCardTarget.classList.remove('d-none')
    }

    hideResults() {
        this.progressCardTarget.classList.add('d-none')
        this.errorCardTarget.classList.add('d-none')
        this.successCardTarget.classList.add('d-none')
    }
}
